import { Link } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState } from 'react';
import { ArrowRight, BookOpen, Users, Award, Briefcase, Star, ChevronDown, Sparkles, Brain, Code2, GraduationCap, Database, CheckCircle, TrendingUp, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { courseCategories } from '@/data/courseCategories';
import { fetchCourseSummaries, type CourseSummary } from '@/lib/api';
// The inquiry form sits well below the fold but drags Radix Select and
// floating-ui (~150KB) into whatever chunk imports it. Loading it lazily keeps
// that off the homepage's critical path.
const InquiryForm = lazy(() => import('@/components/InquiryForm'));
import SmartImage from '@/components/SmartImage';
import { srcSetFor } from '@/lib/responsiveImages';
import { useAnimatedCounter } from '@/hooks/useScrollReveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import heroBg from '@/assets/hero-bg.webp';
import heroVideoAsset from '@/assets/hero-video.mp4.asset.json';
import { setJsonLd, setPageSeo } from '@/lib/seo';
import { getCourseImages } from '@/data/courseImages';
import catErp from '@/assets/cat-erp.webp';
import catProgramming from '@/assets/cat-programming.webp';
import catAi from '@/assets/cat-ai.webp';
import catManagement from '@/assets/cat-management.webp';
import catInternship from '@/assets/cat-internship.webp';

import { highFetchPriority } from '@/lib/utils';
const categoryImages: Record<string, string> = {
  erp: catErp,
  programming: catProgramming,
  ai: catAi,
  management: catManagement,
  internship: catInternship,
};

const categoryIcons: Record<string, React.ReactNode> = {
  Database: <Database className="h-8 w-8" />,
  Code2: <Code2 className="h-8 w-8" />,
  Brain: <Brain className="h-8 w-8" />,
  GraduationCap: <GraduationCap className="h-8 w-8" />,
  Briefcase: <Briefcase className="h-8 w-8" />,
};

const stats = [
  { label: 'Students Trained', value: 5000, suffix: '+', icon: Users },
  { label: 'Active Programs', value: 50, suffix: '+', icon: BookOpen },
  { label: 'Career Placements', value: 2500, suffix: '+', icon: Briefcase },
  { label: 'Expert Trainers', value: 35, suffix: '+', icon: Award },
];

const whyChoose = [
  { icon: CheckCircle, title: 'Industry-Expert Trainers', desc: 'Learn from professionals with 10+ years of real-world experience in top companies.' },
  { icon: Briefcase, title: 'Placement Support', desc: 'Dedicated placement cell with 200+ hiring partners and career counseling.' },
  { icon: BookOpen, title: 'Practical Learning', desc: 'Hands-on projects, live case studies, and real-world simulations — not just theory.' },
  { icon: Award, title: 'Recognized Certifications', desc: 'Industry-recognized certificates that add weight to your resume.' },
  { icon: TrendingUp, title: 'Career Growth Focus', desc: 'Structured career roadmaps, mock interviews, and resume building workshops.' },
  { icon: Zap, title: 'Flexible Learning', desc: 'Online and offline modes with weekend batches for working professionals.' },
];

const testimonials = [
  { name: 'Arun Kumar', role: 'ERP FICO Consultant at TCS', rating: 5, text: 'ASB Training Hub transformed my career. The ERP FICO training was practical, and I got placed within 2 months of completing the course.' },
  { name: 'Priya Nair', role: 'Python Developer at Infosys', rating: 5, text: 'The Python Full Stack course was comprehensive. The internship experience gave me the confidence to crack interviews at top MNCs.' },
  { name: 'Rahul Menon', role: 'AI Engineer at Wipro', rating: 5, text: 'Best AI training institute in Trivandrum! The hands-on projects and mentor support were exceptional.' },
  { name: 'Sneha Das', role: 'HR Manager at UST', rating: 5, text: 'The HR Management diploma helped me transition from a fresher to an HR professional in just 6 months.' },
];

const faqs = [
  { q: 'What makes ASB Training Hub different from other institutes?', a: 'We focus on practical, job-oriented training with industry-expert trainers, real projects, internship support, and dedicated placement assistance. Our curriculum is constantly updated to match industry demands.' },
  { q: 'Do you offer online classes?', a: 'Yes! We offer both online and offline modes. Our online classes are live and interactive with the same quality as in-person sessions.' },
  { q: 'Is there any placement guarantee?', a: 'We provide dedicated placement support including resume building, mock interviews, and connections with 200+ hiring partners. While we don\'t guarantee placement, our track record speaks for itself.' },
  { q: 'Can working professionals join?', a: 'Absolutely! We have weekend and evening batches designed specifically for working professionals looking to upskill.' },
];

const heroVideoSources = ['/hero-video.mp4', heroVideoAsset.url].filter(Boolean);

function HeroAnimationFallback() {
  return (
    <div className="relative h-full w-full overflow-hidden bg-[hsl(20,30%,6%)]" aria-hidden>
      <img
        src={heroBg}
        srcSet={srcSetFor(heroBg)}
        sizes="100vw"
        alt=""
        {...highFetchPriority}
        decoding="async"
        title="ASB Training Hub career training animated background"
        className="hero-fallback-image absolute inset-0 h-full w-full object-cover"
      />
      <div className="hero-animated-fallback">
        <span className="hero-data-ribbon hero-data-ribbon-a" />
        <span className="hero-data-ribbon hero-data-ribbon-b" />
        <span className="hero-data-ribbon hero-data-ribbon-c" />
        <span className="hero-pulse-panel hero-pulse-panel-a" />
        <span className="hero-pulse-panel hero-pulse-panel-b" />
      </div>
    </div>
  );
}

function HeroVideo() {
  const [sourceIndex, setSourceIndex] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(heroVideoSources.length === 0);
  // The clip is ~950KB and purely decorative - the poster carries the hero on
  // its own. Holding it back until the page has painted stops it competing
  // with the LCP text for bandwidth.
  const [videoAllowed, setVideoAllowed] = useState(false);
  const videoSrc = heroVideoSources[sourceIndex];

  useEffect(() => {
    setVideoFailed(heroVideoSources.length === 0);
  }, []);

  useEffect(() => {
    if (heroVideoSources.length === 0) return;

    // Respect the visitor's stated preferences before spending their data.
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;

    if (
      prefersReducedMotion ||
      connection?.saveData ||
      (connection?.effectiveType && /(^|-)2g$/.test(connection.effectiveType))
    ) {
      return;
    }

    let timer: number | undefined;
    const start = () => {
      timer = window.setTimeout(() => setVideoAllowed(true), 600);
    };

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('load', start);
    };
  }, []);

  const handleVideoError = () => {
    setVideoReady(false);

    if (sourceIndex < heroVideoSources.length - 1) {
      setSourceIndex(sourceIndex + 1);
    } else {
      setVideoFailed(true);
    }
  };

  return (
    <div className="relative h-full w-full">
      <HeroAnimationFallback />
      {!videoFailed && videoAllowed && videoSrc && (
        <video
          key={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster={heroBg}
          aria-hidden
          title="ASB Training Hub career training video"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${videoReady ? 'opacity-100' : 'opacity-0'}`}
          onCanPlay={() => setVideoReady(true)}
          onLoadedData={() => setVideoReady(true)}
          onError={handleVideoError}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}
    </div>
  );
}

function StatCounter({ stat }: { stat: typeof stats[0] }) {
  const { ref, count } = useAnimatedCounter(stat.value);
  return (
    <div ref={ref} className="text-center">
      <stat.icon className="h-8 w-8 mx-auto mb-3 text-primary" />
      <div className="text-3xl md:text-4xl font-bold text-white font-heading">
        {count.toLocaleString()}{stat.suffix}
      </div>
      <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
    </div>
  );
}

export default function Index() {
  // Fetched rather than bundled: the homepage shows whatever six courses the
  // admin currently has, and the 100KB catalogue no longer ships in the entry
  // chunk.
  const [popularCourses, setPopularCourses] = useState<CourseSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchCourseSummaries()
      .then((list) => { if (!cancelled) setPopularCourses(list.slice(0, 6)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setPageSeo({
      title: 'ASB Training Hub | ERP, ERP, AI & Programming Courses in Trivandrum',
      description: 'Job-oriented ERP, AI, programming, management and internship courses near Technopark, Trivandrum, with practical training and placement support.',
      keywords: 'ASB Training Hub, best training institute Trivandrum, ERP training Trivandrum, ERP courses Kerala, AI training Trivandrum, programming courses Kerala, internship programs Trivandrum',
      path: '/',
    });
    setJsonLd('organization', {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      '@id': 'https://www.asbtraininghub.com/#organization',
      name: 'ASB Training Hub',
      alternateName: 'ASB Training Hub Trivandrum',
      url: 'https://www.asbtraininghub.com/',
      description:
        'Career training institute in Trivandrum offering job-oriented ERP, AI, programming, management, and internship courses with placement support.',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.asbtraininghub.com/site-logo.png',
        width: 512,
        height: 512,
      },
      image: 'https://www.asbtraininghub.com/site-logo.png',
      telephone: '+918714773304',
      email: 'info@asbtraininghub.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '105-2, The Atomic, Near Technopark Phase 1, Kazhakootam',
        addressLocality: 'Trivandrum',
        addressRegion: 'Kerala',
        postalCode: '695581',
        addressCountry: 'IN',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 8.5644,
        longitude: 76.8792,
      },
      openingHours: 'Mo-Sa 09:00-18:00',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '09:00',
          closes: '18:00',
        },
      ],
      areaServed: { '@type': 'State', name: 'Kerala' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Course catalogue',
        itemListElement: courseCategories.map((category) => ({
          '@type': 'OfferCatalog',
          name: category.label,
          url: `https://www.asbtraininghub.com/courses/${category.id}`,
        })),
      },
      sameAs: [
        'https://www.facebook.com/share/1CsFkSP9E2/',
        'https://www.instagram.com/asbtraininghub',
        'https://www.linkedin.com/company/asb-training-hub/',
        'https://www.youtube.com/@ASBTrainingHub',
        'https://x.com/Asbtraininghub',
      ],
    });
  }, []);

  return (
    <main>
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <HeroVideo />
          <div className="hero-motion-layer" aria-hidden />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/70 to-navy/90" />
          <div className="pointer-events-none absolute -top-4 -left-14 h-[26rem] w-[26rem] rounded-full animate-float will-change-transform" style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 65%)' }} />
          <div className="pointer-events-none absolute -bottom-12 -right-14 h-[32rem] w-[32rem] rounded-full animate-float will-change-transform" style={{ background: 'radial-gradient(circle, hsl(var(--secondary) / 0.12) 0%, transparent 65%)', animationDelay: '1s' }} />
        </div>
        <div className="container mx-auto px-4 pt-24 pb-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-2 mb-6 text-sm text-gray-300">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>Best Training Institute in Trivandrum, Kerala</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white font-heading leading-tight mb-6 animate-fade-in">
              Transform Your Career with{' '}
              <span className="gradient-text">Industry-Focused Training</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Master ERP, AI, Programming, Management & more with expert-led practical training, internship support, and career-oriented learning at ASB Training Hub.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <Link to="/courses" title="Explore ASB Training Hub courses" className="inline-flex self-center">
                <Button size="lg" className="gradient-primary border-0 text-white font-semibold text-lg px-8 py-6 hover:opacity-90">
                  Explore Courses <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="https://wa.me/918714773304?text=Hi%20ASB%20Training%20Hub%2C%20I%20want%20to%20know%20about%20courses" target="_blank" rel="noopener noreferrer" title="Chat with ASB Training Hub on WhatsApp" className="inline-flex self-center">
                <Button size="lg" variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10 text-lg px-8 py-6">
                  WhatsApp Now
                </Button>
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-6 w-6 text-gray-400" />
        </div>
      </section>

      {/* Stats */}
      <section className="relative -mt-16 z-10 container mx-auto px-4">
        <div className="glass-dark rounded-2xl p-8 md:p-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => <StatCounter key={stat.label} stat={stat} />)}
        </div>
      </section>

      {/* Categories */}
      <section className="section-padding bg-background">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">Our Programs</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading mt-2">Featured Course Categories</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">Choose from 50+ industry-focused courses designed to launch or accelerate your career.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseCategories.map((cat) => (
              <Link key={cat.id} to={`/courses/${cat.id}`} title={`${cat.label} | ASB Training Hub`} className="group block">
                <div className="relative overflow-hidden rounded-2xl border border-border bg-card hover-lift h-full">
                  <div className="h-40 relative overflow-hidden">
                    <img src={categoryImages[cat.id]} alt={cat.label} title={`${cat.label} at ASB Training Hub`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                    <div className={`absolute top-3 left-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${cat.color} text-white`}>
                      {categoryIcons[cat.icon]}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-bold font-heading mb-2 group-hover:text-primary transition-colors">{cat.label}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{cat.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary">{cat.count} Courses</span>
                      <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="section-padding gradient-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">Why ASB</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mt-2">Why Choose ASB Training Hub?</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item) => (
              <div key={item.title} className="glass-card rounded-2xl p-6 hover-lift h-full">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-lg font-bold text-white font-heading mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Courses */}
      <section className="section-padding bg-background">
        <div className="container mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">Trending</span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading mt-2">Popular Courses</h2>
            </div>
            <Link to="/courses" title="View all ASB Training Hub courses" className="text-primary font-semibold hover:underline flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularCourses.map((course) => (
              <Link key={course.id} to={`/course/${course.slug}`} title={`${course.title} course | ASB Training Hub`} className="group block">
                <div className="rounded-2xl border border-border bg-card overflow-hidden hover-lift h-full">
                  <div className="h-40 relative overflow-hidden">
                    <SmartImage
                      src={getCourseImages(course.id, course.category).primary}
                      alt={`${course.title} course at ASB Training Hub`}
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      wrapperClassName="absolute inset-0"
                      className="group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 gradient-primary opacity-25 mix-blend-multiply pointer-events-none" />
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-card/90 to-transparent pointer-events-none" />
                    <Code2 className="absolute inset-0 m-auto h-16 w-16 text-white/20" />
                    {course.internship && (
                      <span className="absolute top-3 right-3 bg-green-700 text-white text-xs font-semibold px-2 py-1 rounded-full">Internship</span>
                    )}
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-medium text-primary">{course.categoryLabel}</span>
                    <h3 className="text-lg font-bold font-heading mt-1 mb-2 group-hover:text-primary transition-colors">{course.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{course.description}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{course.duration}</span>
                      <span>{course.mode}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internship Banner */}
      <section className="section-padding">
        <div className="container mx-auto">
          <div className="relative overflow-hidden rounded-3xl gradient-primary p-8 md:p-14 text-center">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_50%,hsla(0,0%,100%,0.1),transparent_50%)]" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4">Internship + Placement Support</h2>
              <p className="text-lg text-white/80 max-w-2xl mx-auto mb-6">Get hands-on industry experience with our internship programs. We partner with 200+ companies to ensure your career takes off.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/courses/internship" title="Explore internship programs at ASB Training Hub" className="inline-flex self-center"><Button size="lg" className="bg-white text-navy font-semibold hover:bg-white/90">Explore Internships</Button></Link>
                <Link to="/apply" title="Apply for admission at ASB Training Hub" className="inline-flex self-center"><Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10">Apply Now</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding gradient-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mt-2">What Our Students Say</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="glass-card rounded-2xl p-6 hover-lift h-full flex flex-col">
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-sm text-gray-300 flex-1 mb-4">"{t.text}"</p>
                <div>
                  <div className="font-semibold text-white text-sm">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/reviews" title="Read ASB Training Hub student reviews" className="inline-flex self-center"><Button variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10">Read More Reviews <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="section-padding bg-background">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-10">
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading mt-2">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border rounded-xl px-4 bg-card">
                <AccordionTrigger className="text-left text-sm font-medium">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-6">
            <Link to="/faq" title="View ASB Training Hub frequently asked questions" className="text-primary font-semibold hover:underline text-sm">View All FAQs →</Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-padding gradient-bg">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">Get Started</span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mt-2 mb-4">Ready to Transform Your Career?</h2>
              <p className="text-gray-400 mb-6">Fill out the inquiry form and our team will get back to you within 24 hours. Or simply WhatsApp us for instant support.</p>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> Free career counseling session</div>
                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> Flexible EMI payment options</div>
                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> 100% placement assistance</div>
                <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> Certificate on completion</div>
              </div>
            </div>
            <div className="glass-card rounded-2xl p-6 md:p-8">
              <h3 className="text-xl font-bold text-white font-heading mb-4">Enquire Now</h3>
              <Suspense
                fallback={<div className="h-[320px] rounded-xl bg-white/5" aria-hidden />}
              >
                <InquiryForm variant="dark" />
              </Suspense>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
